import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import * as jose from 'jose';
import { Observable, firstValueFrom } from 'rxjs';
import { API_URL } from '../../../main';

// Clase base para respuestas de servicios REST
export class ReturnData {
  public data: object | string | boolean | number | any = '';
  public isError = true;
  public ticks = 0;
  public isSecurity = false;
}

// Clase genérica para respuestas tipadas
export class ReturnDataG<T extends any> extends ReturnData {
  public override data: T | null = null;
}

@Injectable()
export class RestService {
  public static expirationDate: Date | null = null;
  public static UrlOffset: string = '';
  public baseUrl: string;

  // Devuelve la URL base con offset
  public get URL(): string {
    return this.baseUrl + RestService.UrlOffset;
  }

  constructor(
    protected http: HttpClient,
    protected injector: Injector,
  ) {
    this.baseUrl = injector.get<string>(API_URL).trim();
  }

  // Decodifica el JWT y obtiene la fecha de expiración
  getTokenExpirationDate(token: string): Date | null {
    const decoded = jose.decodeJwt(token);
    if (decoded.exp === undefined) {
      return null;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  // Obtiene el token de sesión almacenado
  protected getToken(): string | null {
    let token = null;
    try {
      token = sessionStorage.getItem('sessionToken');
    } catch (e) {}
    return token;
  }

  // Fuerza recarga de la página (no se usa actualmente)
  protected forceReload() {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = location.href;
    document.body.appendChild(form);
    form.submit();
  }

  // Manejo de errores HTTP
  protected httpError(err: HttpErrorResponse): void {
    switch (err.status) {
      case 401: {
        //this.forceReload(); // Desactivado por revisión
        break;
      }
      case 403: {
        window.location.href = window.location.origin + '/' + 'login';
        throw Error(
          'You do not have permissions to access the method. Check with the administrator',
        );
      }
    }
  }

  // Maneja la respuesta y actualiza el token si corresponde
  protected responseHandler(response: HttpResponse<object> | undefined) {
    if (response !== undefined) {
      if (response.headers.has('jwt')) {
        let jwt = response.headers.get('jwt');
        if ((jwt ?? '').length > 0) {
          sessionStorage.setItem('sessionToken', jwt!);
          RestService.expirationDate = this.getTokenExpirationDate(jwt!);
        } else {
          RestService.expirationDate = null;
        }
      }
    }
  }

  // Método GET genérico
  protected async get(
    url: string,
    params: string | undefined = undefined,
  ): Promise<ReturnData> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      jwt: this.getToken() ?? '',
    });
    try {
      let data = await firstValueFrom(
        this.http.get(url + (params !== undefined ? params : ''), {
          headers: headers,
          observe: 'response',
        }),
      );
      this.responseHandler(data);
      return data?.body as ReturnData;
    } catch (error) {
      return this.onError(error);
    }
  }

  // Método GET genérico
  protected async patch(
    url: string,
    params: string | undefined = undefined,
  ): Promise<ReturnData> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      jwt: this.getToken() ?? '',
    });
    try {
      let data = (await firstValueFrom(
        this.http.patch(url + (params !== undefined ? params : ''), {
          headers: headers,
          observe: 'response',
        }),
      )) as any as HttpResponse<object> | undefined;
      this.responseHandler(data);
      return data?.body as ReturnData;
    } catch (error) {
      return this.onError(error);
    }
  }

  protected PostStream<T = any>(
    url: string,
    obj: any,
    contentType = 'application/json',
  ): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': contentType,
      accept: 'application/x-ndjson',
      jwt: this.getToken() ?? '',
    });

    return new Observable<T>((observer) => {
      let processedTextLength = 0;
      let buffer = '';

      const flushBuffer = (isFinalChunk = false) => {
        const normalized = buffer.replace(/\r\n/g, '\n');
        const parts = normalized.split('\n');

        if (!isFinalChunk) {
          buffer = parts.pop() ?? '';
        } else {
          buffer = '';
        }

        for (const line of parts) {
          const trimmed = line.trim();
          if (!trimmed) {
            continue;
          }

          try {
            observer.next(JSON.parse(trimmed) as T);
          } catch {
            observer.next(trimmed as unknown as T);
          }
        }
      };

      const subscription = this.http
        .request('POST', url, {
          body: obj,
          headers,
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const partialText: string =
                ('partialText' in event ? (event as any).partialText : '') ??
                '';

              if (partialText.length > processedTextLength) {
                buffer += partialText.slice(processedTextLength);
                processedTextLength = partialText.length;
                flushBuffer(false);
              }
            }

            if (event.type === HttpEventType.Response) {
              const bodyText = event.body ?? '';

              if (bodyText.length > processedTextLength) {
                buffer += bodyText.slice(processedTextLength);
                processedTextLength = bodyText.length;
              }

              this.responseHandler(event as unknown as HttpResponse<object>);
              flushBuffer(true);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
          },
        });

      return () => subscription.unsubscribe();
    });
  }

  // Método POST genérico
  protected async post(
    url: string,
    obj: any,
    contentType = 'application/json',
  ): Promise<ReturnData> {
    const headers = new HttpHeaders({
      'Content-Type': contentType,
      jwt: this.getToken() ?? '',
    });
    try {
      let data = await firstValueFrom(
        this.http.post(url, obj, { headers: headers, observe: 'response' }),
      );
      this.responseHandler(data);
      return data?.body as ReturnData;
    } catch (error) {
      return this.onError(error);
    }
  }

  /**
   * Descarga un archivo desde el backend usando POST.
   * El nombre del archivo se obtiene del header 'Content-Disposition' si está presente.
   */
  protected postDonwload(
    url: string,
    obj: any,
    fileName: string = 'file',
    contentType = 'application/json',
  ): Observable<ReturnData> {
    const headers = new HttpHeaders({
      'Content-Type': contentType,
      jwt: this.getToken() ?? '',
    });
    return new Observable<ReturnData>((observer) => {
      this.http
        .post(url, obj, {
          headers: headers,
          observe: 'response',
          responseType: 'blob' as 'json',
        })
        .toPromise()
        .then((response: any) => {
          // Obtener el nombre del archivo desde el header si existe
          let downloadFileName = fileName;
          const contentDisposition =
            response.headers?.get?.('Content-Disposition') ||
            response.headers?.get?.('content-disposition');
          if (contentDisposition) {
            // Extraer solo el nombre de archivo antes del ';'
            const match = /filename="?([^";]+)"?/.exec(contentDisposition);
            if (match && match[1]) {
              downloadFileName = match[1];
            }
          }
          // Crear el blob y disparar la descarga
          const blob = new Blob([response.body] as BlobPart[], {
            type: 'application/octet-stream',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', downloadFileName);
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
          }, 500);
          observer.next({ data: true, isError: false } as ReturnData);
          observer.complete();
        })
        .catch((error) => {
          observer.next(this.onError(error));
          observer.complete();
        });
    });
  }

  // Método PUT genérico
  protected async put(
    url: string,
    obj: any,
    contentType = 'application/json',
  ): Promise<ReturnData> {
    const headers =
      contentType === 'multipart/form-data'
        ? new HttpHeaders({
            jwt: this.getToken() ?? '',
          })
        : new HttpHeaders({
            'Content-Type': contentType,
            jwt: this.getToken() ?? '',
          });
    try {
      let data = await firstValueFrom(
        this.http.put(url, obj, { headers: headers, observe: 'response' }),
      );
      this.responseHandler(data);
      return data?.body as ReturnData;
    } catch (error) {
      return this.onError(error);
    }
  }

  // Método DELETE genérico
  protected async delete(
    url: string,
    params: string | undefined = undefined,
  ): Promise<ReturnData> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      jwt: this.getToken() ?? '',
    });
    try {
      let data = await firstValueFrom(
        this.http.delete(url + (params !== undefined ? params : ''), {
          headers: headers,
          observe: 'response',
        }),
      );
      this.responseHandler(data);
      return data?.body as ReturnData;
    } catch (error) {
      return this.onError(error);
    }
  }

  // Método para subir archivos al servidor
  public async uploadFile(
    endpoint: string,
    formData: FormData,
    headers: any = {},
  ): Promise<ReturnData> {
    try {
      const options = {
        headers: new HttpHeaders({
          ...headers,
          jwt: this.getToken() ?? '',
          // No establecemos Content-Type aquí, dejamos que el navegador lo maneje con el boundary correcto
        }),
      };

      const response = await firstValueFrom(
        this.http.post<ReturnData>(`${endpoint}`, formData, options),
      );

      return response;
    } catch (error) {
      const returnData = new ReturnData();
      returnData.isError = true;
      returnData.data = error;
      return returnData;
    }
  }

  // Manejo centralizado de errores
  protected onError(err: any): ReturnData {
    try {
      if (err instanceof HttpErrorResponse) {
        this.httpError(err);
      }
      return { data: err, isError: true } as ReturnData;
    } catch (errorCatch) {
      return { data: (errorCatch as any).message, isError: true } as ReturnData;
    }
  }
}

// Servicio CRUD genérico para entidades
@Injectable()
export class RestCrudService<T extends object> extends RestService {
  public Controller = '';
  public ApiVersion = 'v1';

  public Find(page: Page): Promise<ReturnData> {
    return this.post(
      `${this.baseUrl}api/${this.ApiVersion}/${this.Controller}/find`,
      page,
    );
  }

  public Edit(id: any): Promise<ReturnData> {
    return this.get(
      `${this.baseUrl}api/${this.ApiVersion}/${this.Controller}/Edit/${id}`,
    );
  }

  public Save(model: T): Promise<ReturnData> {
    return this.put(
      `${this.baseUrl}api/${this.ApiVersion}/${this.Controller}/Save`,
      model,
    );
  }

  public Delete(id: any): Promise<ReturnData> {
    return this.delete(
      `${this.baseUrl}api/${this.ApiVersion}/${this.Controller}/Delete/${id}`,
    );
  }
}

// Clases auxiliares para paginación y filtrado
export class Page {
  constructor(data: Page | null = null) {
    if (data !== null) {
      this.pageIndex = data.pageIndex ?? this.pageIndex;
      this.pageCount = data.pageCount ?? this.pageCount;
      this.pageSize = data.pageSize ?? this.pageSize;
      this.recordCount = data.recordCount ?? this.recordCount;
      this.filter = data.filter ?? this.filter;
      this.order = data.order ?? this.order;
    }
  }

  public pageIndex = 1;
  public pageCount = 0;
  public pageSize = 25;
  public recordCount = 0;
  public filter: Array<PagingFilter> = new Array<PagingFilter>();
  public order: Array<PagingOrder> = new Array<PagingOrder>();
  public data: Array<object> | null = null;
}

export class PageGeneric<T extends object> extends Page {
  public override data: Array<T> = new Array<T>();
}

export class PagingFilter {
  public property = '';
  public condition:
    | '>'
    | '>='
    | '<'
    | '<='
    | '='
    | '!='
    | 'con'
    | '=|T'
    | 'X'
    | 'x'
    | 'in'
    | 'notin'
    | 'inarray' = 'con';
  public value: string | number | Date | boolean | object = '';
  public valueType = '';
}

export class PagingOrder {
  public property = '';
  public direction: '+' | '-' = '+';
}
